// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Glint — the menus, help, about and results markup. Results follow principle #9: a solo
// screen shows what you cleared out of what was there (the knowable "perfect"), and the
// multiplayer screen shows EVERY player's score + how far each got, not just this tab's.

import { escapeHtml, fmtScore, fmtTime } from './dom';
import { MODE_IDS, modeOf, type Mode, type ModeId } from './modes';
import type { GameState } from './game';
import type { PeerResult } from './race';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(o: { modeId: ModeId; best: number; muted: boolean; dateStr: string }): string {
  const mode = modeOf(o.modeId);
  const chips = MODE_IDS.map(
    (id) =>
      `<button class="chip${id === o.modeId ? ' on' : ''}" data-mode="${id}" aria-pressed="${id === o.modeId}">${escapeHtml(modeOf(id).name)}</button>`,
  ).join('');
  return `
    <section class="screen menu">
      <h1 class="logo">Glint</h1>
      <p class="tagline">Two cards always share exactly one symbol. Spot it. Tap it. Fast.</p>
      <div class="modes" role="group" aria-label="Mode">${chips}</div>
      <p class="mode-blurb">${escapeHtml(mode.blurb)}</p>
      <div class="menu-actions">
        <button class="btn primary" id="playSolo">Play</button>
        <button class="btn" id="playDaily">Daily deck · ${escapeHtml(o.dateStr)}</button>
        <button class="btn" id="playFriends">Play with friends</button>
      </div>
      <p class="best-line">${o.best > 0 ? `Best in ${escapeHtml(mode.name)}: <strong>${fmtScore(o.best)}</strong>` : 'No score yet — go find a glint.'}</p>
      <div class="menu-links">
        <button class="linkbtn" id="help">How to play</button>
        <button class="linkbtn" id="about">About</button>
        <button class="linkbtn" id="mute">${o.muted ? 'Sound: off' : 'Sound: on'}</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>How to play</h2>
    <p>Two cards sit on screen. By a hard mathematical rule they share <strong>exactly one</strong>
    symbol — never none, never two. Find it and tap it: a new card deals the instant you do.</p>
    <ul class="help-list">
      <li>Chain clean taps to build a <strong>combo multiplier</strong> — the faster you spot it, the more it scores.</li>
      <li><strong>Sprint</strong> — clear the whole deck as fast as you can.</li>
      <li><strong>Blaze</strong> — grab as many as you can in 90 seconds; a wrong tap costs time.</li>
      <li><strong>Streak</strong> — one wrong tap ends the run. How far can your nerve carry you?</li>
    </ul>
    <p class="help-controls"><strong>Controls:</strong> tap a symbol. On a keyboard, press number keys
    <kbd>1</kbd>–<kbd>8</kbd> for the bottom card, or move the highlight with the arrow keys and press
    <kbd>Enter</kbd>. Pause any time.</p>
    <button class="btn primary sheet-close">Got it</button>`;
}

export function aboutHtml(): string {
  return `
    <button class="sheet-close" aria-label="Close">×</button>
    <h2>About Glint</h2>
    <p>A fast visual-search game. The deck is a <em>finite projective plane</em> — a bit of
    classical mathematics that guarantees any two cards meet in exactly one symbol, so there is
    always one answer and it is unique. All symbols, art and sound are generated in the browser;
    nothing is downloaded.</p>
    <p>Play solo, chase the UTC <strong>daily deck</strong>, or share a room and race friends on the
    identical deck.</p>
    <p class="about-privacy"><strong>Multiplayer &amp; privacy.</strong> Rooms are peer-to-peer over
    WebRTC — there is no game server. A public signalling relay only helps two browsers find each
    other; no game data is stored on any server, and no cookies or trackers are used beyond an
    anonymous, cookie-less page-view count.</p>
    <p>${ATTRIB}</p>`;
}

// ── results ─────────────────────────────────────────────────────────────────────────────
function statLine(s: GameState): string {
  const bits: string[] = [];
  if (s.mode.finite) {
    bits.push(`Cleared <strong>${s.cleared}/${s.totalPairs}</strong>`);
    bits.push(`Time <strong>${fmtTime(s.elapsedMs)}</strong>`);
  } else if (s.mode.clock === 'down') {
    bits.push(`Cleared <strong>${s.cleared}</strong> cards`);
  } else {
    bits.push(`Reached <strong>${s.cleared}</strong> cards`);
  }
  bits.push(`Best combo <strong>×${s.bestCombo}</strong>`);
  if (!s.mode.sudden) bits.push(`Misses <strong>${s.misses}</strong>`);
  return bits.join(' · ');
}

export function soloResultsHtml(o: { state: GameState; best: number; isNewBest: boolean; daily: boolean }): string {
  const s = o.state;
  const headline = s.mode.sudden && !s.won ? 'One slip — run over' : s.mode.finite && s.won ? 'Deck cleared!' : "Time's up";
  return `
    <section class="screen results">
      <h2 class="results-head">${escapeHtml(headline)}</h2>
      <div class="score-big">${fmtScore(s.score)}</div>
      ${o.isNewBest ? '<div class="new-best">New best!</div>' : `<div class="prev-best">Best in ${escapeHtml(s.mode.name)}: ${fmtScore(o.best)}</div>`}
      <p class="stat-line">${statLine(s)}</p>
      ${o.daily ? '<p class="daily-note">Everyone gets the same daily deck.</p>' : ''}
      <button class="linkbtn results-feedback" id="rfeedback">Report a problem</button>
    </section>`;
}

export function multiResultsHtml(ranked: PeerResult[], selfSeat: number | null, mode: Mode, tally: number[]): string {
  const rows = ranked
    .map((r, i) => {
      const isSelf = r.seat === selfSeat;
      const rank = r.left ? '—' : `#${i + 1}`;
      const winner = i === 0 && !r.left && ranked.filter((x) => !x.left).length > 1;
      const wins = tally[r.seat] ?? 0;
      const detail = r.left ? 'left the room' : `${r.cleared} cleared · ×${r.bestCombo} combo`;
      return `
        <div class="mres-row${isSelf ? ' self' : ''}${r.left ? ' left' : ''}">
          <span class="mres-rank">${rank}</span>
          <span class="mres-name">${winner ? '★ ' : ''}${escapeHtml(r.name)}${isSelf ? ' (you)' : ''}${wins > 0 ? ` <em>· ${wins}★</em>` : ''}</span>
          <span class="mres-detail">${detail}</span>
          <span class="mres-score">${r.left ? '' : fmtScore(r.score)}</span>
        </div>`;
    })
    .join('');
  return `
    <h2 class="results-head">${escapeHtml(mode.name)} · results</h2>
    <div class="mres-list">${rows}</div>`;
}
